import { anomaly } from './diagnostics';

/**
 * Well-known event types for common game events.
 * Layers can use these or define their own string-based event types.
 */
export const GameEventTypes = {
  // Construction events (Parkington layer)
  CONSTRUCTION_PHASE_CHANGED: 'CONSTRUCTION_PHASE_CHANGED',
  CELL_STATE_CHANGED: 'CELL_STATE_CHANGED',
  TOOL_SELECTED: 'TOOL_SELECTED',
  GRADING_VIBRATION: 'GRADING_VIBRATION',
  EXCAVATION_STARTED: 'EXCAVATION_STARTED',
  CONCRETE_POURED: 'CONCRETE_POURED',
  PARKING_LOT_COMPLETE: 'PARKING_LOT_COMPLETE',

  // Colony events (Formicarium layer)
  ANT_BORN: 'ANT_BORN',
  ANT_DIED: 'ANT_DIED',
  ANT_NAMED: 'ANT_NAMED',
  ANT_EXITED: 'ANT_EXITED',
  ANT_ENTERED: 'ANT_ENTERED',
  TUNNEL_COLLAPSED: 'TUNNEL_COLLAPSED',
  CHAMBER_DESTROYED: 'CHAMBER_DESTROYED',
  COLONY_ALERT: 'COLONY_ALERT',
  FOOD_DISCOVERED: 'FOOD_DISCOVERED',
  WEDDING_FLIGHT: 'WEDDING_FLIGHT',
  QUEEN_DIED: 'QUEEN_DIED',
  COLONY_EXTINCT: 'COLONY_EXTINCT',

  // UI events
  BOTTOM_SCREEN_VIEWED: 'BOTTOM_SCREEN_VIEWED',
  ACHIEVEMENT_UNLOCKED: 'ACHIEVEMENT_UNLOCKED',
  NOTIFICATION_SHOW: 'NOTIFICATION_SHOW',
  STAT_UPDATED: 'STAT_UPDATED',

  // Portal/transfer events
  ENTITY_TRANSFERRED: 'ENTITY_TRANSFERRED',
  PORTAL_ACTIVATED: 'PORTAL_ACTIVATED',

  // Surface events
  ANT_KILLED_ON_SURFACE: 'ANT_KILLED_ON_SURFACE',

  // Multi-colony events
  COLONY_SELECT_REQUEST: 'COLONY_SELECT_REQUEST', // Request to change selection (any layer can emit)
  COLONY_SELECTED: 'COLONY_SELECTED',             // Authoritative notification (only ColonyManager emits)
  COLONY_RESTARTED: 'COLONY_RESTARTED',

  // Settings events
  CONFIG_CHANGED: 'CONFIG_CHANGED',
  SETTINGS_IMPORTED: 'SETTINGS_IMPORTED',
} as const;

/**
 * Legacy type alias for backwards compatibility.
 * New code should use string-based event types directly.
 */
export type GameEventType = string;

/**
 * Enhanced game event with source/target routing.
 */
export interface GameEvent {
  /** Event type - can be any string, not limited to enum */
  type: string;

  /** Layer that emitted the event (optional for legacy compatibility) */
  source?: string;

  /** Target layer(s) - 'broadcast' or specific layer ID */
  target?: string | 'broadcast';

  /** Event payload */
  payload: unknown;

  /**
   * Emission order. Monotonic across the bus, from zero.
   *
   * This was `timestamp: performance.now()` until the workspace split, and it was
   * the last wall clock left in a module a settlement worker loads — recorded as
   * the clock debt in `packages/core/tests/purity.test.ts` and in
   * `docs/determinism.md`'s known gaps. Dropping the `DOM` library from this
   * package collected the debt: `performance` is no longer a name this file can
   * write.
   *
   * A counter rather than an injected clock, because nothing had ever read the
   * stamp — not a renderer, not a test, not the transfer path (`LayerManager`
   * reads `TransferableEntity.timestamp`, which is sim-time and unrelated). It
   * was write-only for as long as it existed, the same shape of finding as
   * ADR-009's portal pixels. So this answers the only question the field could
   * have been asked, *which of these two came first*, and it answers it without
   * a host: an event bus with three colonies emitting into it has an order, and
   * it does not have a time of day.
   */
  seq: number;
}

type EventCallback = (payload: unknown, event?: GameEvent) => void;

/**
 * Layer event type registration for documentation/validation.
 */
interface LayerEventRegistry {
  layerId: string;
  eventTypes: string[];
}

export class EventBus {
  private static instance: EventBus;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private history: GameEvent[] = [];
  private readonly MAX_HISTORY = 1000;

  /** Next value for `GameEvent.seq`. */
  private nextSeq = 0;

  /** Registered layer event types for documentation/debugging */
  private layerEventTypes: Map<string, Set<string>> = new Map();

  private constructor() {}

  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * Register event types for a layer (for documentation/validation).
   * This doesn't restrict what events a layer can emit, just documents them.
   */
  registerEventTypes(layerId: string, eventTypes: string[]): void {
    if (!this.layerEventTypes.has(layerId)) {
      this.layerEventTypes.set(layerId, new Set());
    }
    const types = this.layerEventTypes.get(layerId)!;
    for (const type of eventTypes) {
      types.add(type);
    }
  }

  /**
   * Get registered event types for a layer.
   */
  getLayerEventTypes(layerId: string): string[] {
    const types = this.layerEventTypes.get(layerId);
    return types ? Array.from(types) : [];
  }

  /**
   * Get all registered layers and their event types.
   */
  getAllLayerEventTypes(): LayerEventRegistry[] {
    return Array.from(this.layerEventTypes.entries()).map(([layerId, types]) => ({
      layerId,
      eventTypes: Array.from(types),
    }));
  }

  /**
   * Subscribe to an event type.
   * @param type Event type to listen for
   * @param callback Handler function
   * @returns Unsubscribe function
   */
  on(type: string, callback: EventCallback): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(type)?.delete(callback);
    };
  }

  /**
   * Unsubscribe from an event type.
   */
  off(type: string, callback: EventCallback): void {
    this.listeners.get(type)?.delete(callback);
  }

  /**
   * Emit an event (legacy signature for backwards compatibility).
   */
  emit(type: string, payload: unknown): void;

  /**
   * Emit an event with source/target routing.
   */
  emit(type: string, payload: unknown, source?: string, target?: string | 'broadcast'): void;

  emit(type: string, payload: unknown, source?: string, target?: string | 'broadcast'): void {
    const event: GameEvent = {
      type,
      payload,
      source,
      target: target ?? 'broadcast',
      seq: this.nextSeq++,
    };

    // Add to history
    this.history.push(event);
    if (this.history.length > this.MAX_HISTORY) {
      this.history.shift();
    }

    // Notify listeners
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(payload, event);
        } catch (error) {
          // Swallowed on purpose: one bad listener must not stop the others, and
          // must not stop the tick. `anomaly` is where it goes instead of
          // `console`, which this package can no longer name.
          anomaly(`Error in event handler for ${type}`, error);
        }
      });
    }
  }

  /**
   * Emit an event only to a specific target layer.
   * The callback receives the full event object to check target.
   */
  emitTo(type: string, payload: unknown, source: string, target: string): void {
    this.emit(type, payload, source, target);
  }

  /**
   * Subscribe to events from a specific source layer.
   */
  onFrom(type: string, sourceLayer: string, callback: EventCallback): () => void {
    const wrappedCallback: EventCallback = (payload, event) => {
      if (event?.source === sourceLayer) {
        callback(payload, event);
      }
    };

    return this.on(type, wrappedCallback);
  }

  /**
   * Subscribe to events targeted to a specific layer.
   */
  onTo(type: string, targetLayer: string, callback: EventCallback): () => void {
    const wrappedCallback: EventCallback = (payload, event) => {
      if (event?.target === 'broadcast' || event?.target === targetLayer) {
        callback(payload, event);
      }
    };

    return this.on(type, wrappedCallback);
  }

  /**
   * Get event history, optionally filtered by type.
   */
  getHistory(type?: string): GameEvent[] {
    if (type) {
      return this.history.filter((e) => e.type === type);
    }
    return [...this.history];
  }

  /**
   * Get event history filtered by source layer.
   */
  getHistoryFromSource(source: string): GameEvent[] {
    return this.history.filter((e) => e.source === source);
  }

  /**
   * Clear event history.
   */
  clearHistory(): void {
    this.history = [];
  }

  /**
   * Reset the EventBus instance (useful for testing).
   */
  static reset(): void {
    if (EventBus.instance) {
      EventBus.instance.listeners.clear();
      EventBus.instance.history = [];
      EventBus.instance.layerEventTypes.clear();
      EventBus.instance.nextSeq = 0;
    }
  }
}
